import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/store/hooks';
import { hasModuleFeature } from '@/features/auth/moduleFeatures';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { useConfirm } from '@/contexts/confirmContext';
import { chatwootAdminApi } from '../api/chatwoot.api';
import type { ChatwootChannel, CreateChatwootChannelResponse } from '../types';
import {
  ChatwootChannelForm,
  type ChatwootChannelFormValues,
  type InstanceBotOption,
} from '../components/ChatwootChannelForm';
import { ChatwootChannelDetailDialog } from '../components/ChatwootChannelDetailDialog';
import { ChatwootCredentialsDialog } from '../components/ChatwootCredentialsDialog';

type BotLabelMap = Record<string, string>;

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

export function ChatwootChannelsPage() {
  const currentUser = useAppSelector(selectCurrentUser);
  const { confirm } = useConfirm();
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const hasChatwoot = hasModuleFeature(currentUser, 'chatwoot-integration');

  const [channels, setChannels] = useState<ChatwootChannel[]>([]);
  const [botLabels, setBotLabels] = useState<BotLabelMap>({});
  const [instanceBotOptions, setInstanceBotOptions] = useState<InstanceBotOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detailChannel, setDetailChannel] = useState<ChatwootChannel | null>(null);
  const [credentials, setCredentials] = useState<{
    apiAccessToken: string;
    hmacSignatureKey: string | null;
    agentBotApiKey: string | null;
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!hasChatwoot) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const tenantId = isSuperAdmin ? undefined : currentUser?.tenantId || undefined;
      const [channelList, instances] = await Promise.all([
        chatwootAdminApi.listChannels(),
        chatbotAdminApi.listInstances(tenantId),
      ]);
      setChannels(channelList);

      const labels: BotLabelMap = {};
      const usedIds = new Set(channelList.map(channel => channel.instanceBotId));
      const available: InstanceBotOption[] = [];

      for (const instance of instances) {
        const bots = await chatbotAdminApi.listBots(instance.id);
        for (const bot of bots) {
          if (!bot.id) {
            continue;
          }
          const label = `${instance.name} / ${bot.name}`;
          labels[bot.id] = label;
          if (!usedIds.has(bot.id)) {
            available.push({ instanceBotId: bot.id, label });
          }
        }
      }

      setBotLabels(labels);
      setInstanceBotOptions(available);
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar canales Chatwoot');
    } finally {
      setLoading(false);
    }
  }, [hasChatwoot, isSuperAdmin, currentUser?.tenantId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [channels],
  );

  const handleCreate = async (values: ChatwootChannelFormValues) => {
    try {
      setCreating(true);
      const created: CreateChatwootChannelResponse = await chatwootAdminApi.createChannel({
        instanceBotId: values.instanceBotId,
        name: values.name,
        baseUrl: values.baseUrl,
        accountId: values.accountId,
        inboxId: values.inboxId,
        apiAccessToken: values.apiAccessToken,
        hmacSignatureKey: values.hmacSignatureKey || undefined,
        agentBotIdentifier: values.agentBotIdentifier || undefined,
        agentBotApiKey: values.agentBotApiKey || undefined,
        handoffOnAssignment: values.handoffOnAssignment,
        handoffKeyword: values.handoffKeyword || undefined,
        resumeKeyword: values.resumeKeyword || undefined,
        idleHandoffMinutes: values.idleHandoffMinutes,
      });
      setCreateOpen(false);
      setCredentials({
        apiAccessToken: created.apiAccessToken,
        hmacSignatureKey: created.hmacSignatureKey,
        agentBotApiKey: created.agentBotApiKey,
      });
      await loadData();
      toast.success('Canal Chatwoot creado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al crear canal');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (channel: ChatwootChannel) => {
    const confirmed = await confirm({
      title: 'Eliminar canal',
      message: `¿Eliminar el canal "${channel.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }
    try {
      await chatwootAdminApi.deleteChannel(channel.id);
      if (detailChannel?.id === channel.id) {
        setDetailChannel(null);
      }
      await loadData();
      toast.success('Canal eliminado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al eliminar canal');
    }
  };

  if (!hasChatwoot) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <MessageSquare className='h-5 w-5' />
            Chatwoot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>
            La integración Chatwoot no está habilitada para tu tenant. Activá la feature{' '}
            <code className='text-xs'>chatwoot-integration</code> en la configuración del tenant.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold flex items-center gap-2'>
            <MessageSquare className='h-6 w-6' />
            Canales Chatwoot
          </h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Conectá inboxes de Chatwoot con bots Mati, handoff a agentes humanos y webhooks
            firmados.
          </p>
        </div>
        <Button type='button' onClick={() => setCreateOpen(true)}>
          <Plus className='h-4 w-4 mr-2' />
          Nuevo canal
        </Button>
      </div>

      <Card>
        <CardContent className='pt-6'>
          {loading ? (
            <p className='text-sm text-muted-foreground'>Cargando canales…</p>
          ) : sortedChannels.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No hay canales Chatwoot. Creá uno y configurá el webhook en tu inbox.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Bot / instancia</TableHead>
                  <TableHead>Inbox</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Actualizado</TableHead>
                  <TableHead className='text-right'>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedChannels.map(channel => (
                  <TableRow key={channel.id}>
                    <TableCell className='font-medium'>{channel.name}</TableCell>
                    <TableCell className='text-sm'>
                      {botLabels[channel.instanceBotId] ?? channel.instanceBotId}
                    </TableCell>
                    <TableCell className='text-sm font-mono'>{channel.inboxId}</TableCell>
                    <TableCell>
                      <Badge variant={channel.status === 'ACTIVE' ? 'secondary' : 'outline'}>
                        {channel.status}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {formatDate(channel.updatedAt)}
                    </TableCell>
                    <TableCell className='text-right space-x-2'>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => setDetailChannel(channel)}
                      >
                        Detalle
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => void handleDelete(channel)}
                        title='Eliminar'
                      >
                        <Trash2 className='h-4 w-4 text-red-500' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Nuevo canal Chatwoot</DialogTitle>
          </DialogHeader>
          <ChatwootChannelForm
            instanceBotOptions={instanceBotOptions}
            submitting={creating}
            onSubmit={values => void handleCreate(values)}
            onCancel={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <ChatwootCredentialsDialog
        open={Boolean(credentials)}
        apiAccessToken={credentials?.apiAccessToken ?? ''}
        hmacSignatureKey={credentials?.hmacSignatureKey ?? null}
        agentBotApiKey={credentials?.agentBotApiKey ?? null}
        onClose={() => setCredentials(null)}
      />

      <ChatwootChannelDetailDialog
        channel={detailChannel}
        botLabel={
          detailChannel ? (botLabels[detailChannel.instanceBotId] ?? detailChannel.instanceBotId) : ''
        }
        open={Boolean(detailChannel)}
        onOpenChange={open => !open && setDetailChannel(null)}
        onChannelUpdated={() => void loadData()}
      />
    </div>
  );
}
